import React, { useState, useEffect, useRef } from "react";
import { ThemeProvider, createTheme } from "@mui/material";
import QRCode from "react-qr-code";
import { sendLoadingPostRequest } from "../../request/request";
import { MObywatelResult } from "../../types/MObywatelResult";
import { isMobile } from "react-device-detect";

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-marginDense": {
            transform: "translate(14px, 20px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(1) !important",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

interface IStepMObywatelForm {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  mObywatelGuid: string;
  isMObywatelDone: boolean;
  setIsMObywatelDone: React.Dispatch<React.SetStateAction<boolean>>;
}

const StepMObywatel = ({
  setStep,
  mObywatelGuid,
  isMObywatelDone,
  setIsMObywatelDone,
}: IStepMObywatelForm) => {
  const [open, setOpen] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyCodeToClipboard = async (): Promise<boolean> => {
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(mObywatelGuid);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = mObywatelGuid;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (!copied) return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  const checkMObywatel = async () => {
    const { data: response } = await sendLoadingPostRequest<
      undefined,
      MObywatelResult
    >({
      loadingFunction: undefined,
      endpoint: "agreement/CheckMObywatel",
    });

    if (response?.data?.result === true) {
      setIsMObywatelDone(true);
      setStep(7);
      return;
    }

    const interval = response?.data?.interval ?? 1;
    const delay = 1000 * interval;

    if (response?.data?.mock === true) {
      timeoutRef.current = setTimeout(() => {
        setIsMObywatelDone(true);
        setStep(7);
      }, delay);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      checkMObywatel();
    }, delay);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isMObywatelDone === true) {
      setStep(7);
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    checkMObywatel();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <ThemeProvider theme={defaultMaterialTheme}>
      <hr />

      <p className="bold red section mb-2">Weryfikacja mObywatel</p>
      <p>
        Potwierdź swoją tożsamość za pośrednictwem <b>aplikacji mObywatel</b>
      </p>
      <div className="graySection" style={{ marginTop: "30px" }}>
        <div className="qrCodeSection">
          <div className="qrCode">
            <div className="qrCodeBox">
              <div className="qrCodeBackground">
                <QRCode
                  size={256}
                  style={{
                    height: "auto",
                    maxWidth: "100%",
                    width: "100%",
                    padding: "15px",
                  }}
                  value={mObywatelGuid}
                  viewBox={`0 0 256 256`}
                />
              </div>
            </div>
          </div>
          <div className="qrCodeText">
            <p className="bold red section qrCodeTextSize mb-4">
              Zeskanuj Kod QR
            </p>
            <div className="qrCodeList">
              <p>
                1. Otwórz <b>aplikację mObywatel</b> na swoim urządzeniu
                mobilnym.
              </p>
              <p>
                2. Wejdź w zakładkę Kod QR znajdującą się w panelu dolnym i
                wybierz opcję "<b>Skanuj Kod QR</b>".
              </p>
              <p>
                3. Zeskanuj wygenerowany kod i{" "}
                <b>potwierdź udostępnienie danych.</b>
              </p>
            </div>
          </div>
        </div>
      </div>
      {isMobile ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: "30px",
          }}
        >
          <button
            onClick={async () => {
              const copied = await copyCodeToClipboard();
              if (copied) setOpen((prev) => !prev);
            }}
            className="nextBtn"
          >
            {open ? "Skopiowano" : "Skopiuj kod"}
          </button>
        </div>
      ) : null}

      {open ? (
        <>
          <br />
          <div className="qrCodeList" style={{ marginTop: "30px" }}>
            <p>
              1. Zaloguj się do aplikacji <b>mObywatel</b>.
            </p>
            <p>
              2. Z menu na dole ekranu aplikacji wybierz przycisk <b>Kod QR.</b>
            </p>
            <p>
              3. Wybierz przycisk <b>Zeskanuj kod QR.</b>
            </p>
            <p>
              4. Wybierz przycisk <b>Wpisz kod.</b>
            </p>
            <p>
              5. Wklej kod i wybierz przycisk <b>Dalej.</b>
            </p>
            <p>
              6. Użyj przycisku <b>Udostępnij dane,</b> aby przejść do usługi
            </p>
          </div>
        </>
      ) : null}
      <p className="mt-4">
        Po pozytywnej autoryzacji nastąpi automatyczne przekierowanie do
        kolejnego kroku.
      </p>
      <p className="mt-4">
        O ile nie jesteście Państwo użytkownikiem aplikacji mObywatel, nie
        możemy z Państwem dokończyć zdalnie procesu zawierania umowy. Aby
        ukończyć proces zawarcia umowy w sposób zdalny niezbędne jest pobranie i
        aktywacja aplikacji mObywatel - więcej informacji o tej aplikacji
        znajdziecie Państwo na stronie{" "}
        <a
          className="link red"
          href="https://info.mobywatel.gov.pl/o-aplikacji"
          target="_blank"
        >
          https://info.mobywatel.gov.pl/o-aplikacji
        </a>
        .
      </p>
      <p className="mt-4">
        W przypadku braku możliwości ukończenia procesu z wykorzystaniem
        aplikacji mObywatel zapraszamy Państwa do Punktu Obsługi Klienta. Pełna
        lista Punktów Obsługi Klienta Xelion dostępna jest na stronie{" "}
        <a
          className="link red"
          href="https://www.xelion.pl/placowki"
          target="_blank"
        >
          https://www.xelion.pl/placowki
        </a>
        . Możecie Państwo również zostawić swoje dane na stronie{" "}
        <a
          className="link red"
          href="https://www.xelion.pl/kontakt-z-partnerem-xelion"
          target="_blank"
        >
          https://www.xelion.pl/kontakt-z-partnerem-xelion
        </a>{" "}
        celem umówienia z Partnerem Xelion, który w miejscu z Państwem
        uzgodnionym przeprowadzi czynności zmierzające do zawarcia umowy.
      </p>
      <button
        onClick={() => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setStep(3);
        }}
        className="backBtn"
        type="submit"
      >
        Cofnij
      </button>
    </ThemeProvider>
  );
};

export { StepMObywatel };
